/** @param {Date} d */
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Monday-based week start (ISO week) */
export const getWeekStartMonday = (date) => {
  const d = startOfDay(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

export const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

export const formatShortDate = (date, locale = undefined) =>
  new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(date);

export const formatRangeLabel = (start, end) =>
  `${formatShortDate(start)} – ${formatShortDate(end)}`;

/**
 * Human-readable week span for grids and headers.
 * Examples: "Apr 6 – 12, 2026" (same month); "Apr 30 – May 6, 2026" (split months, same year);
 * full dates when the range crosses years.
 */
export const formatWeekRangeReadable = (start, end, locale = undefined) => {
  const a = start instanceof Date ? start : start != null ? new Date(start) : null;
  const b = end instanceof Date ? end : end != null ? new Date(end) : null;
  if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return "";

  const sy = a.getFullYear();
  const sm = a.getMonth();
  const sd = a.getDate();
  const ey = b.getFullYear();
  const em = b.getMonth();
  const ed = b.getDate();

  if (sy === ey && sm === em) {
    const month = new Intl.DateTimeFormat(locale, { month: "short" }).format(a);
    return `${month} ${sd} – ${ed}, ${sy}`;
  }
  if (sy === ey) {
    const left = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(a);
    const right = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(b);
    return `${left} – ${right}, ${sy}`;
  }
  return formatRangeLabel(a, b, locale);
};

/** UTC midnight ISO for the local calendar date (API weekStartDate / weekEndDate query params). */
export const toUtcDateOnlyISO = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  return new Date(Date.UTC(y, m, day, 0, 0, 0)).toISOString();
};

/** Local calendar Monday week key YYYY-MM-DD (for mock / API alignment). */
export const getWeekStartKey = (date) => {
  const m = getWeekStartMonday(date);
  const y = m.getFullYear();
  const mo = String(m.getMonth() + 1).padStart(2, "0");
  const d = String(m.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
};

/**
 * Parse API date/datetime as a local civil date (noon) so `YYYY-MM-DD` and UTC midnight strings
 * do not shift the calendar day in non-UTC timezones.
 * @param {unknown} raw
 * @returns {Date | null}
 */
export const parseCivilDateFromApi = (raw) => {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return null;
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate(), 12, 0, 0, 0);
  }
  const s = String(raw).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
    const local = new Date(y, mo - 1, d, 12, 0, 0, 0);
    return Number.isNaN(local.getTime()) ? null : local;
  }
  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/**
 * Monday start date for GET UserTimeSheet/weekly and navigation.
 * Time log rows may use Sun–Sat labels with weekStart on a Sunday; ISO Monday for that period is the next day.
 * @param {unknown} input
 * @returns {Date | null}
 */
export const getTimesheetNavWeekStartDate = (input) => {
  const d = parseCivilDateFromApi(input);
  if (!d || Number.isNaN(d.getTime())) return null;
  const sod = startOfDay(d);
  const day = sod.getDay();
  if (day === 0) {
    const nextMon = addDays(sod, 1);
    nextMon.setHours(0, 0, 0, 0);
    return nextMon;
  }
  return getWeekStartMonday(sod);
};

/** YYYY-MM-DD key for {@link getTimesheetNavWeekStartDate} (always a Monday). */
export const getTimesheetNavWeekStartKey = (input) => {
  const mon = getTimesheetNavWeekStartDate(input);
  return mon ? getWeekStartKey(mon) : null;
};

/**
 * Inverse of getWeekStartKey: key encodes the Monday (local) — do not re-apply getWeekStartMonday
 * or a Sunday-looking key would jump to the previous week.
 */
export const parseLocalWeekStartKey = (key) => {
  if (!key || typeof key !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  const localStart = new Date(y, mo - 1, d, 0, 0, 0, 0);
  if (Number.isNaN(localStart.getTime())) return null;
  return localStart;
};

export const formatWeekdayShort = (date, locale = undefined) =>
  new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date);

export const formatWeekdayLong = (date, locale = undefined) =>
  new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date);

/** Weekly time log day column: "Monday, Apr 27, 2026" (locale-aware). */
export const formatTimeLogWeeklyDayLabel = (date, locale = undefined) => {
  const d = date instanceof Date ? date : date != null ? new Date(date) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${formatWeekdayLong(d, locale)}, ${formatShortDate(d, locale)}`;
};

/** First day of calendar month */
export const startOfMonth = (date) => {
  const d = startOfDay(date);
  d.setDate(1);
  return d;
};

/** Last day of calendar month */
export const endOfMonth = (date) => {
  const d = startOfDay(date);
  d.setMonth(d.getMonth() + 1, 0);
  return d;
};

export const addMonths = (date, n) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
};

/** e.g. "April 2026" */
export const formatMonthYearLabel = (date, locale = undefined) =>
  new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(date);

/** Label for full month span */
export const formatMonthRangeLabel = (monthStart, locale = undefined) => {
  const start = startOfMonth(monthStart);
  const end = endOfMonth(monthStart);
  return `${formatShortDate(start, locale)} – ${formatShortDate(end, locale)}`;
};

/**
 * Find two `YYYY-MM-DD` substrings in a string (e.g. API period or notes).
 * @param {string} s
 * @returns {{ start: Date, end: Date } | null}
 */
export const tryParseTwoIsoDatesAsRange = (s) => {
  const t = String(s).trim();
  if (!t) return null;
  const re = /(\d{4}-\d{2}-\d{2})/g;
  const hits = [];
  let m;
  while ((m = re.exec(t)) !== null) {
    hits.push(m[1]);
    if (hits.length >= 2) break;
  }
  if (hits.length < 2) return null;
  const start = parseCivilDateFromApi(hits[0]);
  const end = parseCivilDateFromApi(hits[1]);
  if (!start || !end) return null;
  return { start, end };
};

/**
 * Readable period for approval queue rows: abbreviated month, day, and year (locale-aware),
 * same style as {@link formatWeekRangeReadable} / {@link formatMonthRangeLabel}.
 * Prefers structured dates on the DTO, then parses ISO pairs inside `period`, else trimmed text.
 * @param {Record<string, unknown>} dto
 * @param {string} [locale] `Intl` locale (e.g. i18n language)
 */
export const formatApprovalQueuePeriodFromDto = (dto, locale = undefined) => {
  if (!dto || typeof dto !== "object") return "—";
  const ws = parseCivilDateFromApi(
    dto.weekStartDate ?? dto.WeekStartDate ?? dto.weekRangeStart ?? dto.WeekRangeStart
  );
  const we = parseCivilDateFromApi(
    dto.weekEndDate ?? dto.WeekEndDate ?? dto.weekRangeEnd ?? dto.WeekRangeEnd
  );
  if (ws && we) {
    const label = formatWeekRangeReadable(ws, we, locale);
    if (label) return label;
  }
  if (ws && !we) {
    const label = formatWeekRangeReadable(ws, addDays(ws, 6), locale);
    if (label) return label;
  }
  const y = dto.year ?? dto.Year;
  const mo = dto.month ?? dto.Month;
  if (y != null && mo != null) {
    const nY = Number(y);
    const nM = Number(mo);
    if (Number.isFinite(nY) && Number.isFinite(nM) && nM >= 1 && nM <= 12) {
      return formatMonthRangeLabel(new Date(nY, nM - 1, 15), locale);
    }
  }
  const raw = dto.period ?? dto.Period;
  if (raw != null && String(raw).trim() !== "") {
    const str = String(raw).trim();
    const pair = tryParseTwoIsoDatesAsRange(str);
    if (pair) {
      const label = formatWeekRangeReadable(pair.start, pair.end, locale);
      if (label) return label;
    }
    return str;
  }
  return "—";
};

/**
 * Monday starts of each week that overlaps the calendar month of `monthStart`.
 */
export const getWeekStartsOverlappingMonth = (monthStart) => {
  const start = startOfMonth(monthStart);
  const end = endOfMonth(monthStart);
  let ws = getWeekStartMonday(start);
  if (addDays(ws, 6) < start) {
    ws = addDays(ws, 7);
  }
  const out = [];
  let cur = new Date(ws);
  while (cur <= end) {
    const weekEnd = addDays(cur, 6);
    if (weekEnd >= start && cur <= end) {
      out.push(new Date(cur));
    }
    cur = addDays(cur, 7);
  }
  return out;
};

/** True if the Monday–Sunday week identified by `weekStartKey` overlaps the calendar month of `monthAnchor`. */
export const weekStartKeyOverlapsMonth = (weekStartKey, monthAnchor) => {
  const ws = parseLocalWeekStartKey(weekStartKey);
  if (!ws) return false;
  const we = addDays(ws, 6);
  const ms = startOfMonth(monthAnchor);
  const me = endOfMonth(monthAnchor);
  return ws <= me && we >= ms;
};
